import { useEffect, useState, type FormEvent } from 'react';
import { api } from '@/api/client';
import { useAuth } from '../state/AuthContext';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function SettingsProfileScreen() {
  const { user, refresh } = useAuth();
  const [displayName, setDisplayName] = useState(user?.profile?.displayName ?? user?.fullName ?? '');
  const [jobTitle, setJobTitle] = useState(user?.profile?.jobTitle ?? '');
  const [timezone, setTimezone] = useState(user?.profile?.timezone ?? '');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [avatarRevision, setAvatarRevision] = useState(0);
  const [busyAvatar, setBusyAvatar] = useState(false);

  useEffect(() => {
    setDisplayName(user?.profile?.displayName ?? user?.fullName ?? '');
    setJobTitle(user?.profile?.jobTitle ?? '');
    setTimezone(user?.profile?.timezone ?? '');
  }, [user]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await api.authUpdateProfile({
        displayName,
        fullName: displayName,
        jobTitle: jobTitle || null,
        timezone: timezone || null,
      });
      await refresh();
      setMessage('Profile saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function onAvatarFile(file: File | null) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file (PNG, JPEG, WebP).');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be under 2MB.');
      return;
    }
    setBusyAvatar(true);
    setError(null);
    try {
      await api.authUploadAvatar(file);
      await refresh();
      setAvatarRevision((n) => n + 1);
      setMessage('Profile image updated.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyAvatar(false);
    }
  }

  async function onRemoveAvatar() {
    setBusyAvatar(true);
    setError(null);
    try {
      await api.authDeleteAvatar();
      await refresh();
      setAvatarRevision((n) => n + 1);
      setMessage('Profile image removed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyAvatar(false);
    }
  }

  const hasAvatar = Boolean(user?.profile?.avatarMediaAssetId);

  return (
    <section className="panel stack" data-testid="settings-profile">
      <h2>Profile</h2>
      <div className="settings-avatar-row">
        <div className="settings-avatar" aria-hidden="true">
          {hasAvatar ? (
            <img src={`/api/auth/avatar?r=${avatarRevision}`} alt="" />
          ) : (
            <span>{initials(displayName || user?.email || '?')}</span>
          )}
        </div>
        <div className="stack">
          <label className="btn secondary compact">
            {busyAvatar ? 'Uploading…' : 'Upload image'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              disabled={busyAvatar}
              onChange={(e) => void onAvatarFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <button
            type="button"
            className="btn secondary compact"
            disabled={busyAvatar || !hasAvatar}
            onClick={() => void onRemoveAvatar()}
          >
            Remove image
          </button>
        </div>
      </div>
      <form className="stack" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="profile-name">Display name</label>
          <input
            id="profile-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="profile-title">Job title</label>
          <input id="profile-title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="profile-tz">Timezone</label>
          <input
            id="profile-tz"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="e.g. Europe/London"
          />
        </div>
        <p className="muted">Email: {user?.email}</p>
        {error ? <p className="error">{error}</p> : null}
        {message ? <p className="muted">{message}</p> : null}
        <button className="btn" type="submit">
          Save profile
        </button>
      </form>
    </section>
  );
}
